import { useMemo } from "react";
import type { TaskFilterParams } from "@/hooks/useTasks";
import { useTaskFilterStore } from "@/stores/useTaskFilterStore";

type User = { id: string; azureObjectId?: string | null };

/**
 * Builds the `TaskFilterParams` sent to the backend from the current store
 * state, the committed (debounced) search term, and the cascading-scope
 * key-sets from `useCascadingFilterScopes`.
 *
 * Out-of-scope child filter values are silently omitted here so the backend
 * never sees a contradictory AND of parent+child filters. They remain in the
 * store so selections reactivate when a parent filter changes back.
 */
export function useServerFilterParams({
  committedSearch,
  validProjectIdSet,
  validPlotIdSet,
  validPpKeySet,
  validSectionKeySet,
  validLocationIdSet,
  ppNameMap,
  sectionNameMap,
  myProjectRolesLoading,
  seesAllProjects,
  currentAzureUserId,
  users,
}: {
  committedSearch: string;
  validProjectIdSet: Set<string>;
  validPlotIdSet: Set<string>;
  validPpKeySet: Set<string>;
  validSectionKeySet: Set<string>;
  validLocationIdSet: Set<string>;
  ppNameMap: Map<string, string[]>;
  sectionNameMap: Map<string, string[]>;
  myProjectRolesLoading: boolean;
  /** Viewer whose server-side project visibility is org-wide (ADMIN / INTERNAL+). */
  seesAllProjects: boolean;
  currentAzureUserId: string | undefined;
  users: User[];
}): TaskFilterParams {
  const {
    projectFilter,
    plotFilter,
    procurementPackageFilter,
    workSectionFilter,
    locationFilter,
    assignedToFilter,
    deadlineWeek,
    taskTypeFilter,
    sortBy,
    sortDirection,
    viewFilter,
    onlyMyProjects,
  } = useTaskFilterStore();

  return useMemo<TaskFilterParams>(() => {
    const params: TaskFilterParams = {};

    if (committedSearch) params.search = committedSearch;

    // Project filter: send the effective set of project IDs.
    //  - Explicit projectFilter (intersected with validProjectIdSet when
    //    "Vain omat hankkeet" is on, so an out-of-scope remembered value
    //    doesn't AND to an empty result set).
    //  - If no explicit filter but "Vain omat hankkeet" is on, narrow the
    //    task query to the user's member projects. Skipped for ADMIN /
    //    INTERNAL+, whose scope is every project anyway — sending the full id
    //    list would only bloat each query.
    if (projectFilter.length > 0) {
      const effectiveProjectIds = onlyMyProjects
        ? projectFilter.filter((id) => validProjectIdSet.has(id))
        : projectFilter;
      if (effectiveProjectIds.length > 0) params.projectIds = effectiveProjectIds;
    } else if (onlyMyProjects && !seesAllProjects && !myProjectRolesLoading) {
      const memberIds = Array.from(validProjectIdSet);
      if (memberIds.length > 0) params.projectIds = memberIds;
    }

    // Plot filter: only send the in-scope subset. See comment in
    // useCascadingFilterScopes for the rationale.
    const effectivePlotIds = plotFilter.filter((id) => validPlotIdSet.has(id));
    if (effectivePlotIds.length > 0) params.plotIds = effectivePlotIds;

    // Procurement package filter: extract names from in-scope composite keys.
    // Keys are "CODE|||NAME" (uppercased); the backend matches case-insensitively.
    const effectivePpKeys = procurementPackageFilter.filter((k) =>
      validPpKeySet.has(k),
    );
    if (effectivePpKeys.length > 0) {
      const names = new Set<string>();
      effectivePpKeys.forEach((key) => {
        const ppNames = ppNameMap.get(key) || [];
        ppNames.forEach((n) => names.add(n));
      });
      params.procurementPackageNames = Array.from(names);
    }

    const effectiveSectionKeys = workSectionFilter.filter((k) =>
      validSectionKeySet.has(k),
    );
    if (effectiveSectionKeys.length > 0) {
      const names = new Set<string>();
      effectiveSectionKeys.forEach((key) => {
        const sNames = sectionNameMap.get(key) || [];
        sNames.forEach((n) => names.add(n));
      });
      params.sectionNames = Array.from(names);
    }

    const effectiveLocationIds = locationFilter.filter((id) =>
      validLocationIdSet.has(id),
    );
    if (effectiveLocationIds.length > 0) params.locationIds = effectiveLocationIds;

    // Assigned user: "Minun" view filters by the current user on the backend.
    if (viewFilter === "Minun" && currentAzureUserId) {
      const currentUser = users.find((u) => u.azureObjectId === currentAzureUserId);
      if (currentUser) params.responsibleUserId = currentUser.id;
    }

    if (assignedToFilter.length > 0) params.responsibleUserIds = assignedToFilter;
    if (taskTypeFilter.length > 0) params.taskTypes = taskTypeFilter;

    // Deadline-week selection → server fields.
    //  - { kind: 'all' }        → no constraint
    //  - { kind: 'week', … }    → year + week number
    //  - { kind: 'noDeadline' } → noDeadline: true
    if (deadlineWeek.kind === "week") {
      params.deadlineWeekYear = deadlineWeek.year;
      params.deadlineWeekNumber = deadlineWeek.week;
    } else if (deadlineWeek.kind === "noDeadline") {
      params.noDeadline = true;
    }

    params.sortBy = sortBy;
    params.sortDirection = sortDirection;

    return params;
  }, [
    committedSearch,
    projectFilter,
    plotFilter,
    procurementPackageFilter,
    workSectionFilter,
    ppNameMap,
    sectionNameMap,
    validPlotIdSet,
    validPpKeySet,
    validSectionKeySet,
    validLocationIdSet,
    validProjectIdSet,
    locationFilter,
    onlyMyProjects,
    myProjectRolesLoading,
    seesAllProjects,
    viewFilter,
    currentAzureUserId,
    users,
    assignedToFilter,
    taskTypeFilter,
    deadlineWeek,
    sortBy,
    sortDirection,
  ]);
}

/**
 * Derive the filter params for the week-bucket query from the task-list
 * params by stripping the deadline-week selection. The buckets must reflect
 * "what would be there if no week filter were applied", so every other filter
 * (project, assignee, search, status, …) is preserved.
 */
export function buildBucketFilterParams(
  filterParams: TaskFilterParams,
): TaskFilterParams {
  const rest = { ...filterParams };
  delete rest.deadlineWeekYear;
  delete rest.deadlineWeekNumber;
  delete rest.noDeadline;
  return rest;
}
